import React,{createContext,useCallback,useContext,useEffect,useRef,useState} from 'react';
import {useAuth} from './authContext';
import {useFarm} from './farmContext';
import {useGpsSession} from './GpsSessionContext';
import {db} from './db';
import {guestRequest,GuestApiError,GuestFarm,VisitState} from './guestApi';
import {usePersonalSettings} from './personalSettings';

const empty:VisitState={visits:[],current:null,presence:'unknown'};
type Queued={id:string;path:string;method:string;body:unknown};
interface GuestContextValue extends VisitState {
 farms:GuestFarm[];farm:GuestFarm|null;loading:boolean;error:string;pending:number;offline:boolean;
 refresh:()=>Promise<void>;discardPending:()=>Promise<void>;mutate:(path:string,body:unknown,method?:string)=>Promise<void>;
}
const Context=createContext<GuestContextValue|null>(null);
export const useGuest=()=>{const value=useContext(Context);if(!value)throw new Error('Guest provider required');return value;};
export function GuestProvider({children}:{children:React.ReactNode}){
 const {user}=useAuth(),{activeFarmId}=useFarm(),{state:gps,enabled}=useGpsSession();
 const {settings}=usePersonalSettings(user?.email,user?.roles??(user?.role?[user.role]:[]));
 const [farms,setFarms]=useState<GuestFarm[]>([]),[state,setState]=useState(empty),[error,setError]=useState(''),[loading,setLoading]=useState(true),[pending,setPending]=useState(0),[offline,setOffline]=useState(!navigator.onLine);
 const scope=JSON.stringify([user?.email,user?.status,user?.farm_ids,activeFarmId]);
 const currentScope=useRef(scope);currentScope.current=scope;
 const [loadedScope,setLoadedScope]=useState('');
 const scopedFarms=loadedScope===scope?farms:[];
 const farm=scopedFarms.find(f=>f.farm_id===activeFarmId)||scopedFarms[0]||null;
 const database=db,active=user?.status==='Active',busy=useRef(false),generation=useRef(0),lastObservation=useRef(0);
 const currentFarm=useRef(farm?.farm_id);currentFarm.current=farm?.farm_id;
 const refresh=useCallback(async()=>{
  if(!active||currentScope.current!==scope)return;
  const version=++generation.current;
  const current=()=>database===db&&currentScope.current===scope&&version===generation.current;
  try{
   const available=await guestRequest<GuestFarm[]>('/guest/farms');
   if(!current())return;setFarms(available);
   await database.workspaceMeta.put({key:'guest-farms',value:available});
   const target=available.find(f=>f.farm_id===activeFarmId)||available[0];
   if(target){
    const result=await guestRequest<VisitState>('/guest/visits?farm_id='+encodeURIComponent(target.farm_id));
    if(!current())return;setState(result);
    await database.workspaceMeta.put({key:'guest-visits:'+target.farm_id,value:result});
   }else setState(empty);
   setOffline(false);setError('');
  }catch(err){
   if(!current())return;
   if(err instanceof GuestApiError&&![408,429,500,502,503,504].includes(err.status)){setError(err.message);if(err.status===403){setFarms([]);setState(empty);}return;}
   setOffline(true);
   const savedFarms=(await database.workspaceMeta.get('guest-farms'))?.value||[];
   if(!current())return;setFarms(savedFarms);
   const target=savedFarms.find((f:GuestFarm)=>f.farm_id===activeFarmId)||savedFarms[0];
   const saved=target?(await database.workspaceMeta.get('guest-visits:'+target.farm_id))?.value:null;
   if(current())setState({...saved||empty,presence:'unknown'});
  }finally{if(current()){setLoadedScope(scope);setLoading(false);}}
 },[active,activeFarmId,database,scope]);
 const flush=useCallback(async()=>{
  if(busy.current||!active||!navigator.onLine||currentScope.current!==scope)return;
  const current=()=>database===db&&currentScope.current===scope;
  busy.current=true;
  try{
   let queue:Queued[]=(await database.workspaceMeta.get('guest-queue'))?.value||[];if(!current())return;setPending(queue.length);
   while(queue.length&&current()){
    const item=queue[0];await guestRequest(item.path,item.method,item.body);
    await database.transaction('rw',database.workspaceMeta,async()=>{
     const all:Queued[]=(await database.workspaceMeta.get('guest-queue'))?.value||[];
     queue=all.filter(q=>q.id!==item.id);await database.workspaceMeta.put({key:'guest-queue',value:queue});
    });
    if(current())setPending(queue.length);
   }
   if(current())await refresh();
  }catch(err){if(current())setError((err as Error).message+' Your pending change is retained.');}
  finally{busy.current=false;}
 },[active,database,refresh,scope]);
 const mutate=async(path:string,body:unknown,method='POST')=>{
  setError('');
  // Store before submitting. The same visit id is replayed after connection loss.
  await database.transaction('rw',database.workspaceMeta,async()=>{
   const queue:Queued[]=(await database.workspaceMeta.get('guest-queue'))?.value||[];
   if(queue.length)throw new Error('Sync your pending change before making another visit change.');
   const id=crypto.randomUUID();
   queue.push({id,path,method,body:{...(body as object),operation_id:id}});
   await database.workspaceMeta.put({key:'guest-queue',value:queue});setPending(queue.length);
  });
  await flush();
 };
 const discardPending=async()=>{
  if(busy.current)throw new Error('Wait until the current sync finishes.');
  await database.workspaceMeta.delete('guest-queue');setPending(0);setError('');await refresh();
 };
 useEffect(()=>{
  if(!active){setFarms([]);setState(empty);setLoading(false);return;}
  let live=true;
  void database.workspaceMeta.get('guest-queue').then(row=>{if(database===db)setPending(row?.value?.length||0);});
  void refresh().then(()=>{if(live)void flush();});
  const online=()=>{void flush();},off=()=>{setOffline(true);setState(s=>({...s,presence:'unknown'}));};
  const foreground=()=>{if(document.visibilityState==='visible')void flush();};
  window.addEventListener('online',online);window.addEventListener('offline',off);document.addEventListener('visibilitychange',foreground);
  const timer=setInterval(foreground,60000);
  return()=>{live=false;generation.current++;clearInterval(timer);window.removeEventListener('online',online);window.removeEventListener('offline',off);document.removeEventListener('visibilitychange',foreground);};
 },[active,database,refresh,flush]);
 useEffect(()=>{
  const p=gps.position;
  if(!active||!enabled||!settings.locationEnabled||!p||!farm||pending||!navigator.onLine||document.visibilityState!=='visible')return;
  if(p.timestamp<=lastObservation.current||p.timestamp-lastObservation.current<15000||Date.now()-p.timestamp>120000)return;
  lastObservation.current=p.timestamp;
  const farmId=farm.farm_id;
  void guestRequest<VisitState>('/guest/visits/observe','POST',{farm_id:farmId,point:{lat:p.coords.latitude,lng:p.coords.longitude,accuracy:p.coords.accuracy,ts:p.timestamp}})
   .then(result=>{if(database===db&&currentFarm.current===farmId){setState(result);void database.workspaceMeta.put({key:'guest-visits:'+farmId,value:result});}})
   .catch(()=>{/* Manual check-in remains available; next fresh observation retries. */});
 },[gps.position,active,enabled,settings.locationEnabled,farm,pending,database]);
 return <Context.Provider value={{...(loadedScope===scope?state:empty),farms:scopedFarms,farm,loading:loading||(active&&loadedScope!==scope),error,pending,offline,refresh:flush,discardPending,mutate}}>{children}</Context.Provider>;
}
